import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native';
import { ActivityIndicator, IconButton, List } from 'react-native-paper';

import { DocumentIcon } from '@/assets';
import { ListSection } from '@/features/list/components/ListSection';
import type { DocumentItemPlaceholder } from '@/features/media/types/picker';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import type { SVGComponent } from '@/types';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      paddingVertical: 0,
      paddingHorizontal: 0,
      marginVertical: 0,
      backgroundColor: 'transparent',
      borderRadius: 12
    },
    listItem: {
      borderColor: theme.colors.outlineVariant,
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderRightWidth: 1
    },
    listItemFirst: {
      borderTopRightRadius: 12,
      borderTopLeftRadius: 12,
      borderTopWidth: 1
    },
    listItemLast: {
      borderBottomLeftRadius: 12,
      borderBottomRightRadius: 12,
      borderBottomWidth: 1
    }
  });

type Props = {
  style?: StyleProp<ViewStyle>;
  attachments: DocumentItemPlaceholder[];
  onDelete?: (item: DocumentItemPlaceholder) => void;
  actionIcon: SVGComponent;
  uploading?: boolean;
};

export const AttachmentList: FC<Props> = ({
  style,
  attachments,
  onDelete,
  actionIcon,
  uploading = false
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { t } = useTranslation(LocalizationNameSpace.MEDIA, {
    keyPrefix: 'components.attachment-list'
  });

  const handleOpenAttachment = useCallback(
    (attachment: DocumentItemPlaceholder) => {
      handleOpenUrl(attachment.documentItem?.url);
    },
    []
  );

  return (
    <ListSection style={[styles.container, style]}>
      {attachments?.map((attachment, index) => (
        <List.Item
          // Todo: Workaround for: https://github.com/callstack/react-native-paper/issues/4810#issuecomment-3445337084
          borderless
          key={attachment.documentItemId ?? attachment.temporaryDocumentItemId}
          style={[
            styles.listItem,
            index === 0 && styles.listItemFirst,
            index === attachments.length - 1 &&
              !uploading &&
              styles.listItemLast,
            { paddingHorizontal: 16 }
          ]}
          title={attachment.documentItem?.title}
          left={() => <DocumentIcon height={24} width={24} />}
          right={() => (
            <IconButton
              onPress={
                onDelete
                  ? (e) => {
                      e.preventDefault();
                      onDelete(attachment);
                    }
                  : undefined
              }
              style={{ margin: -8 }}
              icon={() =>
                actionIcon({
                  height: 24,
                  width: 24,
                  fill: theme.colors.onSurface
                })
              }
            />
          )}
          onPress={() => handleOpenAttachment(attachment)}
        />
      ))}
      {uploading && (
        <List.Item
          style={[
            styles.listItem,
            styles.listItemLast,
            attachments.length === 0 && styles.listItemFirst,
            { paddingHorizontal: 16 }
          ]}
          key="uploading"
          title={t('uploadingPlaceholder')}
          right={({ color }) => <ActivityIndicator color={color} />}
          left={() => <DocumentIcon height={24} width={24} />}
        />
      )}
    </ListSection>
  );
};
